import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { governanceAPI } from '../utils/api';
import ProposalDetail from '../components/ProposalDetail';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Grid,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  HStack,
  VStack,
  Divider,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Avatar,
  Icon,
  Tag,
  Tooltip,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiArrowDown,
  FiPlusCircle,
  FiClock,
  FiCheck,
  FiX,
  FiZap,
  FiDollarSign,
  FiUsers,
  FiActivity,
  FiStar,
  FiFilter,
  FiBarChart2,
  FiMessageCircle,
  FiSettings,
  FiFileText,
  FiEye
} from 'react-icons/fi';
import './Governance.css';

const Governance = () => {
  const { connected, publicKey } = useWallet();
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({
    active: 6,
    passed: 23,
    rejected: 4,
    failed: 7
  });
  const [nextVoteTime, setNextVoteTime] = useState('25m');
  const [totalVotes, setTotalVotes] = useState(18540);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Theme colors
  const cardBg = useColorModeValue('white', 'dark.card');
  const borderColor = useColorModeValue('gray.200', 'dark.border');
  const textColor = useColorModeValue('gray.800', 'dark.textPrimary');
  const secondaryTextColor = useColorModeValue('gray.600', 'dark.textSecondary');
  const statBg = useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)');

  // Enhanced mock data with Solana addresses
  const mockProposals = [
          {
            id: '1',
      title: 'Buy $WOJAK at market price',
      description: 'Desol AI • Flash Trade',
      proposer: 'DYw8jMTrFWji7h4VTSWQcKxmwBQkYxgCHvKiUn7MTcgV',
      proposerAvatar: 'https://bit.ly/broken-link',
            type: 'trade',
      tradeType: 'flash',
      token: 'WOJAK',
            action: 'buy',
      amount: 5000,
      targetPrice: null,
      timeLeft: '1h 12m',
      createdAt: '2023-09-12T14:30:00Z',
      expiresAt: '2023-09-12T17:30:00Z',
            status: 'active',
      comments: 12,
            votes: {
              yes: 65,
        no: 35,
        total: 154
      },
      analysis: {
        marketCap: '$15.2M',
        volume24h: '$3.5M',
        priceChange24h: '+12.5%',
        sentiment: 'Bullish',
        riskScore: 7.8
      },
      sources: ['CoinGecko', 'DexScreener', 'Solscan', 'Twitter API']
          },
          {
            id: '2',
      title: 'Sell 50% of $TRUMP position',
      description: 'Take profit on recent gains',
      proposer: 'EXU6iBX8qvdYu9bLQUWiKRmQVQ5nSELHqWfJSNVEjXxz',
      proposerAvatar: 'https://bit.ly/broken-link',
            type: 'trade',
      tradeType: 'standard',
            token: 'TRUMP',
            action: 'sell',
      amount: '50%',
      targetPrice: 0.00000042,
      timeLeft: '5h 45m',
      createdAt: '2023-09-12T12:15:00Z',
      expiresAt: '2023-09-13T00:15:00Z',
            status: 'active',
      comments: 28,
            votes: {
        yes: 72,
        no: 28,
        total: 203
      },
      analysis: {
        marketCap: '$42.8M',
        volume24h: '$8.2M',
        priceChange24h: '+32.1%',
        sentiment: 'Very Bullish',
        riskScore: 6.5
      },
      sources: ['CoinGecko', 'DexScreener', 'Solscan', 'Twitter API']
          },
          {
            id: '3',
      title: 'Add $PEPE to whitelist',
      description: 'Add PEPE for future trades',
      proposer: 'FZLEuNjqJFdtSfS9AJAg3nGJNxpLMi3XAKmwYDHHGN9B',
      proposerAvatar: 'https://bit.ly/broken-link',
      type: 'whitelist',
      token: 'PEPE',
      timeLeft: '12h 20m',
      createdAt: '2023-09-12T10:00:00Z',
      expiresAt: '2023-09-13T10:00:00Z',
            status: 'active',
      comments: 8,
            votes: {
        yes: 88,
        no: 12,
        total: 97
      },
      analysis: {
        marketCap: '$320.5M',
        volume24h: '$45.2M',
        priceChange24h: '+5.2%',
        sentiment: 'Neutral',
        riskScore: 5.2
      },
      sources: ['CoinGecko', 'DexScreener', 'Solscan', 'Twitter API']
          },
          {
            id: '4',
      title: 'Increase fast-track threshold to 75%',
      description: 'System parameter update',
      proposer: 'GpYnVDj7jWsJUbzii8PfabK3PgJz1VZwLs5i4XVnYu3S',
      proposerAvatar: 'https://bit.ly/broken-link',
      type: 'parameter',
      parameter: 'fast_track_threshold',
      currentValue: '66%',
      proposedValue: '75%',
      timeLeft: '23h 15m',
      createdAt: '2023-09-12T08:30:00Z',
      expiresAt: '2023-09-14T08:30:00Z',
      status: 'active',
      comments: 15,
            votes: {
        yes: 52,
        no: 48,
        total: 112
      },
      justification: 'Increasing the threshold will ensure that fast-track trades have stronger community consensus before execution, reducing the risk of controversial trades being executed too quickly.',
      impact: 'Medium - This will make fast-track trades slightly harder to approve but will increase security.',
      sources: ['Governance Framework v2.1', 'Community Feedback Survey']
          },
          {
            id: '5',
      title: 'Implement "Buy the dip" strategy for memecoins',
      description: 'Automated strategy for market downturns',
      proposer: 'HqVxzRWf6RZVhksVLgj9SYQCytGEzVrh8NDADJ4F5X1T',
      proposerAvatar: 'https://bit.ly/broken-link',
      type: 'strategy',
      strategyType: 'automated',
      targetTokens: ['WOJAK', 'PEPE', 'BONK'],
      conditions: 'Market dip > 15% in 24h',
      timeLeft: '36h 10m',
      createdAt: '2023-09-11T14:20:00Z',
      expiresAt: '2023-09-15T14:20:00Z',
      status: 'active',
      comments: 32,
            votes: {
        yes: 81,
        no: 19,
        total: 245
      },
      analysis: {
        backtest: {
          period: '6 months',
          profitLoss: '+42.8%',
          winRate: '68%',
          drawdown: '12.5%'
        },
        riskScore: 6.2
      },
      sources: ['Historical Market Data', 'Backtesting Engine', 'Risk Assessment Model']
    },
    {
      id: '6',
      title: 'Flash Trade: Buy $BONK on recent dip',
      description: 'Quick opportunity after market correction',
      proposer: '9ZNTfD4HYMb8EWcJCKVBJtJexn5hZzPRFHjQvQYDNZjY',
      proposerAvatar: 'https://bit.ly/broken-link',
      type: 'trade',
      tradeType: 'flash',
      token: 'BONK',
      action: 'buy',
      amount: 10000,
      targetPrice: null,
      timeLeft: '30m',
      createdAt: '2023-09-12T15:30:00Z',
      expiresAt: '2023-09-12T16:30:00Z',
      status: 'active',
      comments: 5,
      votes: {
        yes: 78,
        no: 22,
        total: 68
      },
      analysis: {
        marketCap: '$85.3M',
        volume24h: '$12.1M',
        priceChange24h: '-8.5%',
        sentiment: 'Bullish (Dip Buying Opportunity)',
        riskScore: 6.8
      },
      sources: ['CoinGecko', 'DexScreener', 'Solscan', 'Twitter API']
    }
  ];

  useEffect(() => {
    fetchProposals();
    // Simulating periodic updates
    const interval = setInterval(() => {
      const countdown = parseInt(nextVoteTime.replace('m', ''));
      if (countdown > 1) {
        setNextVoteTime(`${countdown - 1}m`);
      } else {
        setNextVoteTime('0m');
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchProposals = async () => {
    try {
      setIsLoading(true);
      // Try to fetch from real API
      try {
        const data = await governanceAPI.getProposals('all');
        setProposals(data);
      } catch (error) {
        console.warn('Using mock data due to API error:', error);
        // Fallback to mock data
        setProposals(mockProposals);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error in fetchProposals:', error);
      setIsLoading(false);
    }
  };

  const handleVote = async (proposalId, vote) => {
    if (!connected) {
      alert('Please connect your wallet to vote');
      return;
    }
    
    try {
      // Optimistic UI update
      setProposals(proposals.map(p => {
        if (p.id === proposalId) {
          const updatedVotes = { ...p.votes };
        if (vote === 'yes') {
          updatedVotes.yes += 1;
            updatedVotes.total += 1;
        } else {
          updatedVotes.no += 1;
            updatedVotes.total += 1;
          }
          return { ...p, votes: updatedVotes };
        }
        return p;
      }));
      
      // Try to send vote to API
      try {
        await governanceAPI.voteOnProposal(proposalId, vote, publicKey?.toString());
      } catch (error) {
        console.warn('Vote stored locally due to API error:', error);
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Error submitting your vote. Please try again.');
    }
  };

  // Get badge color based on proposal type
  const getProposalTypeColor = (type) => {
    switch (type) {
      case 'trade':
        return 'green';
      case 'whitelist':
        return 'purple';
      case 'parameter':
        return 'blue';
      case 'strategy':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Get icon based on proposal type
  const getProposalTypeIcon = (type) => {
    switch (type) {
      case 'trade':
        return FiDollarSign;
      case 'whitelist':
        return FiStar;
      case 'parameter':
        return FiSettings;
      case 'strategy':
        return FiActivity;
      default:
        return FiFileText;
    }
  };

  // Render a proposal card
  const renderProposalCard = (proposal) => {
    const isActive = proposal.status === 'active';
    const yesPercentage = proposal.votes.yes;
    const noPercentage = proposal.votes.no;

  return (
      <Card 
        key={proposal.id}
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        mb={4}
        transition="transform 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Flex align="center" mb={2}>
                <Tag 
                  size="sm" 
                  colorScheme={getProposalTypeColor(proposal.type)}
                  borderRadius="full"
                  mr={2}
                >
                  <Icon as={getProposalTypeIcon(proposal.type)} mr={1} />
                  {proposal.type.charAt(0).toUpperCase() + proposal.type.slice(1)}
                </Tag>
                {proposal.tradeType === 'flash' && (
                  <Tag size="sm" colorScheme="yellow" borderRadius="full">
                    <Icon as={FiZap} mr={1} />
                    Fast Trade
                  </Tag>
                )}
              </Flex>
              <Heading size="md" color={textColor}>{proposal.title}</Heading>
              <Text fontSize="sm" color={secondaryTextColor} mt={1}>
                {proposal.description}
              </Text>
            </Box>
            <Flex direction="column" align="flex-end">
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={isActive ? 'green.500' : 'gray.500'}
              >
                {isActive ? (
                  <Flex align="center">
                    <Icon as={FiClock} mr={1} />
                    {proposal.timeLeft} left
                  </Flex>
                ) : (
                  proposal.status
                )}
              </Text>
            </Flex>
          </Flex>
        </CardHeader>
        
        <CardBody pt={0}>
          <Box mt={4}>
            <Flex justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="medium">Yes ({yesPercentage}%)</Text>
              <Text fontSize="sm" fontWeight="medium">No ({noPercentage}%)</Text>
            </Flex>
            <Flex>
              <Progress 
                value={yesPercentage} 
                colorScheme="green" 
                height="8px" 
                borderLeftRadius="full"
                borderRightRadius="none"
                flex={yesPercentage}
                min={0}
              />
              <Progress 
                value={noPercentage} 
                colorScheme="red" 
                height="8px" 
                borderLeftRadius="none"
                borderRightRadius="full"
                flex={noPercentage}
                min={0}
              />
            </Flex>
            <Text fontSize="xs" color={secondaryTextColor} mt={1}>
              {proposal.votes.total} votes total
            </Text>
          </Box>
          
          {/* Fast Trade buttons for trade proposals */}
          {isActive && proposal.type === 'trade' && (
            <HStack mt={3} spacing={2}>
              <Button 
                size="xs" 
                colorScheme={proposal.tradeType === 'flash' ? "yellow" : "green"}
                leftIcon={proposal.tradeType === 'flash' ? <FiZap /> : <FiDollarSign />}
                isDisabled={!connected}
              >
                Fast Trade
              </Button>
            </HStack>
          )}
        </CardBody>
        
        <Divider borderColor={borderColor} />
        
        <CardFooter pt={3} pb={3}>
          <Flex width="100%" direction="column" spacing={3}>
            {/* Proposer info */}
            <Flex width="100%" mb={3}>
              <HStack>
                <Avatar size="xs" name={proposal.proposer} />
                <Text fontSize="xs" color={secondaryTextColor}>
                  {proposal.proposer.substring(0, 4)}...{proposal.proposer.substring(proposal.proposer.length - 4)}
                </Text>
              </HStack>
            </Flex>
            
            {/* Buttons */}
            <Flex width="100%" justify="space-between" align="center">
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                leftIcon={<FiEye />}
                onClick={() => {
                  setSelectedProposal(proposal);
                  onOpen();
                }}
              >
                View Details
              </Button>
              
              {isActive && (
                <HStack spacing={2}>
                  <Button 
                    size="sm" 
                    colorScheme="green" 
                    variant="outline"
                    leftIcon={<FiCheck />}
                    onClick={() => handleVote(proposal.id, 'yes')}
                    isDisabled={!connected}
                  >
                    Yes
                  </Button>
                  <Button 
                    size="sm" 
                    colorScheme="red" 
                    variant="outline"
                    leftIcon={<FiX />}
                    onClick={() => handleVote(proposal.id, 'no')}
                    isDisabled={!connected}
                  >
                    No
                  </Button>
                </HStack>
              )}
            </Flex>
          </Flex>
        </CardFooter>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="50vh" direction="column">
        <Spinner size="xl" color="brand.400" thickness="4px" speed="0.65s" mb={4} />
        <Text color={secondaryTextColor}>Loading governance data...</Text>
      </Flex>
    );
  }

  return (
    <Container maxW="container.xl" py={4}>
      {/* Proposal Detail Modal - Full Screen */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Proposal Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedProposal && (
              <ProposalDetail 
                proposal={selectedProposal} 
                onClose={onClose} 
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading as="h1" size="xl" color={textColor}>Governance</Heading>
          <Text color={secondaryTextColor} mt={1}>Community decision making platform</Text>
        </Box>
        <Box bg={statBg} px={6} py={3} borderRadius="lg" textAlign="right">
          <Text fontWeight="bold" fontSize="xl" color="brand.400">
            {totalVotes.toLocaleString()}
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>
            Total votes cast
          </Text>
          <Text fontSize="xs" fontWeight="medium" color="green.400" mt={1}>
            <Icon as={FiClock} /> Next vote in {nextVoteTime}
          </Text>
        </Box>
      </Flex>
      
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 6 }} mb={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <StatLabel color={secondaryTextColor}>Active</StatLabel>
          <StatNumber color="brand.400">{stats.active}</StatNumber>
          <StatHelpText>
            <Badge colorScheme="green">Live</Badge>
          </StatHelpText>
        </Stat>
        
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <StatLabel color={secondaryTextColor}>Passed</StatLabel>
          <StatNumber color="green.400">{stats.passed}</StatNumber>
          <StatHelpText>
            <Badge colorScheme="green">Approved</Badge>
          </StatHelpText>
        </Stat>
        
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <StatLabel color={secondaryTextColor}>Rejected</StatLabel>
          <StatNumber color="red.400">{stats.rejected}</StatNumber>
          <StatHelpText>
            <Badge colorScheme="red">Not Approved</Badge>
          </StatHelpText>
        </Stat>
        
        <Stat bg={cardBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <StatLabel color={secondaryTextColor}>Failed</StatLabel>
          <StatNumber color="orange.400">{stats.failed}</StatNumber>
          <StatHelpText>
            <Badge colorScheme="orange">Expired</Badge>
          </StatHelpText>
        </Stat>
      </SimpleGrid>
      
      <Box mb={6}>
        <HStack spacing={4} mb={4}>
          <Button 
            leftIcon={<FiPlusCircle />}
            colorScheme="brand"
            size="md"
            isDisabled={!connected}
        >
          Create Proposal
          </Button>
          
          <Button 
            leftIcon={<FiFilter />}
            variant="outline"
            size="md"
          >
            Filter
          </Button>
          
          <Button 
            leftIcon={<FiBarChart2 />}
            variant="outline"
            size="md"
          >
            Analytics
          </Button>
        </HStack>
      </Box>
      
      <Tabs colorScheme="brand" variant="enclosed" mb={6} onChange={(index) => setActiveTab(index)}>
        <TabList>
          <Tab>Active Proposals</Tab>
          <Tab>Trade Proposals</Tab>
          <Tab>Token Whitelist</Tab>
          <Tab>Strategies</Tab>
          <Tab>Parameters</Tab>
          <Tab>History</Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {proposals
                .filter(p => p.status === 'active')
                .map(renderProposalCard)}
                
              {proposals.filter(p => p.status === 'active').length === 0 && (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10} 
                  bg={cardBg} 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <Icon as={FiUsers} boxSize={10} color="gray.400" mb={4} />
                  <Text color={secondaryTextColor}>No active proposals</Text>
                  <Button 
                    mt={4} 
                    colorScheme="brand" 
                    leftIcon={<FiPlusCircle />}
                    size="sm"
                    isDisabled={!connected}
                  >
                    Create a proposal
                  </Button>
                </Flex>
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {proposals
                .filter(p => p.type === 'trade')
                .map(renderProposalCard)}
                
              {proposals.filter(p => p.type === 'trade').length === 0 && (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10} 
                  bg={cardBg} 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <Icon as={FiDollarSign} boxSize={10} color="gray.400" mb={4} />
                  <Text color={secondaryTextColor}>No trade proposals</Text>
                </Flex>
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {proposals
                .filter(p => p.type === 'whitelist')
                .map(renderProposalCard)}
                
              {proposals.filter(p => p.type === 'whitelist').length === 0 && (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10} 
                  bg={cardBg} 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <Icon as={FiStar} boxSize={10} color="gray.400" mb={4} />
                  <Text color={secondaryTextColor}>No whitelist proposals</Text>
                </Flex>
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {proposals
                .filter(p => p.type === 'strategy')
                .map(renderProposalCard)}
                
              {proposals.filter(p => p.type === 'strategy').length === 0 && (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10} 
                  bg={cardBg} 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <Icon as={FiActivity} boxSize={10} color="gray.400" mb={4} />
                  <Text color={secondaryTextColor}>No strategy proposals</Text>
                </Flex>
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <VStack spacing={4} align="stretch">
              {proposals
                .filter(p => p.type === 'parameter')
                .map(renderProposalCard)}
                
              {proposals.filter(p => p.type === 'parameter').length === 0 && (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={10} 
                  bg={cardBg} 
                  borderRadius="lg" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                >
                  <Icon as={FiSettings} boxSize={10} color="gray.400" mb={4} />
                  <Text color={secondaryTextColor}>No parameter change proposals</Text>
                </Flex>
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            <Flex 
              direction="column" 
              align="center" 
              justify="center" 
              py={10} 
              bg={cardBg} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
            >
              <Icon as={FiClock} boxSize={10} color="gray.400" mb={4} />
              <Text color={secondaryTextColor}>Historical proposals will appear here</Text>
            </Flex>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
};

export default Governance;